/**
 * Multisport visual identity — icon, label, and accent for a sport context.
 * Reuses canonical sport normalization. Never assume cycling.
 */

import { Activity, Bike, Footprints, Mountain, Waves } from "lucide-react";
import { TOKENS } from "./designTokens";
import {
  CANONICAL_SPORT_LABELS,
  canonicalSportForFilter,
  isIndoorActivity,
  normalizeActivitySport,
  sportDisplayLabel,
} from "./sportNormalization";

export const SPORT_VISUALS = {
  cycling: {
    icon: Bike,
    accent: TOKENS.analytics.green,
    soft: "rgba(49, 185, 120, 0.14)",
  },
  running: {
    icon: Footprints,
    accent: TOKENS.analytics.blue,
    soft: "rgba(47, 142, 234, 0.14)",
  },
  walking: {
    icon: Footprints,
    accent: TOKENS.analytics.grey,
    soft: "rgba(127, 138, 147, 0.16)",
  },
  hiking: {
    icon: Mountain,
    accent: TOKENS.analytics.green,
    soft: "rgba(49, 185, 120, 0.14)",
  },
  swimming: {
    icon: Waves,
    accent: TOKENS.analytics.teal,
    soft: "rgba(42, 168, 160, 0.16)",
  },
  other: {
    icon: Activity,
    accent: TOKENS.analytics.grey,
    soft: "rgba(127, 138, 147, 0.16)",
  },
};

export function getSportKey(activityOrSport) {
  return canonicalSportForFilter(activityOrSport);
}

export function getSportVisual(activityOrSport = {}) {
  const sport = getSportKey(activityOrSport);
  const visual = SPORT_VISUALS[sport] || SPORT_VISUALS.other;
  const isObject = activityOrSport && typeof activityOrSport === "object";

  let indoor = false;
  let label;
  if (isObject) {
    indoor = isIndoorActivity(activityOrSport);
    label = sportDisplayLabel(activityOrSport);
  } else {
    const normalized = normalizeActivitySport(activityOrSport);
    indoor = Boolean(normalized.is_indoor);
    label = indoor
      ? sportDisplayLabel({
          sport: normalized.sport,
          is_indoor: true,
          sport_subtype: normalized.sport_subtype,
        })
      : CANONICAL_SPORT_LABELS[sport] || CANONICAL_SPORT_LABELS.other;
  }

  let displayLabel = label;
  if (isObject && !indoor && sport === "cycling" && label === "Cycling") {
    displayLabel = "Outdoor Cycling";
  }

  return {
    sport,
    indoor,
    treatment: indoor ? "indoor" : "outdoor",
    icon: visual.icon,
    accent: visual.accent,
    soft: visual.soft,
    label: displayLabel,
  };
}

export function getSportIcon(activityOrSport) {
  return getSportVisual(activityOrSport).icon;
}
